/**
 * Supports snippets/configurator-pdp-extras.liquid + snippets/configurator-shipping-note.liquid.
 *
 * sections/rug-configurator.liquid mounts a bundled app (assets/rug-configurator.js) into
 * #rug-configurator-app that renders its own two-column layout entirely client-side: a left image
 * column and a right `.steps-accordion` column ending in a `.price-bar`. Liquid has no DOM to render
 * standard PDP chrome (title, review link, shipping note, payment info, USP icons) *into* at page
 * load, so configurator-pdp-extras.liquid renders all of that as ordinary hidden markup, and this
 * file physically relocates it into the app's DOM once the app has mounted:
 *   - [data-extra="title"]        -> first child of .steps-accordion (above the step accordion)
 *   - [data-extra="shipping"]     -> right after .price-bar
 *   - [data-extra="payment-info"] -> after the shipping note
 *   - [data-extra="usp"]          -> after that
 *
 * Nodes are moved (not cloned), so the real Liquid-rendered content — including the
 * <configurator-shipping-note> custom element below — keeps working normally once connected.
 */
(function relocateConfiguratorExtras() {
  function relocateOnce() {
    const app = document.querySelector('#rug-configurator-app, #drawer-configurator-app, #seatpad-configurator-app');
    const extras = document.querySelector('[id^="ConfiguratorPdpExtras-"]');
    if (!app || !extras) return false;

    const stepsAccordion = app.querySelector('.steps-accordion');
    const priceBar = app.querySelector('.price-bar');
    if (!stepsAccordion || !priceBar) return false;

    const title = extras.querySelector('[data-extra="title"]');
    const shipping = extras.querySelector('[data-extra="shipping"]');
    const paymentInfo = extras.querySelector('[data-extra="payment-info"]');
    const usp = extras.querySelector('[data-extra="usp"]');

    if (title) stepsAccordion.insertBefore(title, stepsAccordion.firstChild);

    let anchor = priceBar;
    [shipping, paymentInfo, usp].forEach((block) => {
      if (!block) return;
      anchor.insertAdjacentElement('afterend', block);
      anchor = block;
    });

    extras.remove();
    return true;
  }

  if (relocateOnce()) return;

  const app = document.querySelector('#rug-configurator-app, #drawer-configurator-app, #seatpad-configurator-app');
  if (!app) return;
  const observer = new MutationObserver(() => {
    if (relocateOnce()) observer.disconnect();
  });
  observer.observe(app, { childList: true, subtree: true });
})();

/**
 * Keeps .floor-trigger ("Boden auswählen") glued to the sticky image's top-right corner.
 *
 * .magnify-wrapper's own stickiness is plain CSS (configurator-pdp-extras.css) and stays untouched
 * here. .floor-trigger can't just get the same `position: sticky` treatment: it's `position: absolute`
 * in the bundled app's own CSS (removed from the flex row's layout), and switching it to sticky put it
 * back in flow as a flex item next to the image instead of overlaid on it. So this only ever *reads*
 * .magnify-wrapper's current position (whatever the CSS engine already settled on) and writes plain
 * `position: fixed` top/left onto .floor-trigger to match — on scroll/resize only (not every animation
 * frame regardless of movement, which visibly jittered the image on an earlier attempt), and it never
 * moves or recreates .floor-trigger itself, which is a Vue-rendered, Vue-owned element.
 */
(function syncFloorTrigger() {
  function sync() {
    const wrapper = document.querySelector('#rug-configurator-app .magnify-wrapper');
    const trigger = document.querySelector('#rug-configurator-app .floor-trigger');
    if (!wrapper || !trigger) return;
    const rect = wrapper.getBoundingClientRect();
    trigger.style.position = 'fixed';
    trigger.style.top = `${Math.round(rect.top) + 12}px`;
    trigger.style.left = `${Math.round(rect.right - trigger.offsetWidth - 12)}px`;
    trigger.style.right = 'auto';
  }

  let scheduled = false;
  function onScrollOrResize() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      sync();
      scheduled = false;
    });
  }

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  sync();
})();

/**
 * <configurator-shipping-note>
 *
 * Same free-shipping-threshold logic as <free-shipping-note> (assets/free-shipping-note.js), reading
 * the same clarissakork_shipping.free_shipping_rules shop metafield, but there is no product variant
 * to read a price from here — rug-configurator.js computes the price client-side from the chosen
 * width/length (or diameter) and finish, rendering it into `.price-bar .price` only once a price is
 * determinable. This watches that element instead of a variant price:
 *   - no `.price` element yet (nothing configured enough to price) -> note stays hidden entirely
 *   - a price is showing -> combine it with the live cart subtotal, same as the standard PDP note,
 *     and show "zzgl. Versandkosten" or "inkl. gratis Versand" accordingly
 *
 * A configured rug is always a physical product, so there is no digital/"ships by email" state here.
 */
if (!customElements.get('configurator-shipping-note')) {
  class ConfiguratorShippingNote extends HTMLElement {
    connectedCallback() {
      this.country = this.dataset.country;
      this.currency = this.dataset.currency;
      this.freeEl = this.querySelector('.pdp-shipping__free');
      this.paidEl = this.querySelector('.pdp-shipping__paid');
      this.requestId = 0;

      try {
        this.rule = this.findRule(JSON.parse(this.querySelector('[data-free-shipping-rules]')?.textContent || 'null'));
      } catch (error) {
        this.rule = null;
      }
      if (this.rule) this.ensureThresholdLabel();

      this.scheduleCheck = FoxTheme.utils.debounce(() => this.check(), 150);

      this.priceObserver = new MutationObserver(this.scheduleCheck);
      this.observeApp();

      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.scheduleCheck
      );

      this.check();
    }

    disconnectedCallback() {
      this.priceObserver?.disconnect();
      this.cartUpdateUnsubscriber?.();
    }

    // The app's #rug-configurator-app div is in the initial HTML, but the bundle that fills it loads
    // async, so retry briefly if it somehow isn't there yet rather than silently never observing.
    observeApp() {
      const app = document.querySelector('#rug-configurator-app, #drawer-configurator-app, #seatpad-configurator-app');
      if (!app) {
        setTimeout(() => this.observeApp(), 200);
        return;
      }
      this.priceObserver.observe(app, { childList: true, subtree: true, characterData: true });
    }

    // rug-configurator.js renders the price via
    // new Intl.NumberFormat(currency === 'CHF' ? 'de-CH' : 'de-DE', {style:'decimal', minimumFractionDigits:2, maximumFractionDigits:2})
    // prefixed with its currency symbol, e.g. "€ 328,00" (de-DE: '.' thousands/',' decimal) or
    // "CHF 328.00" (de-CH: "'" thousands/'.' decimal). Match just the trailing numeric token so the
    // symbol/prefix never matters, then normalize whichever separator style it used.
    parsePrice(text) {
      const match = (text || '').trim().match(/[\d][\d.,']*$/);
      if (!match) return null;
      let numeric = match[0];
      numeric = numeric.includes(',') ? numeric.replace(/\./g, '').replace(',', '.') : numeric.replace(/'/g, '');
      const value = parseFloat(numeric);
      return Number.isFinite(value) ? value : null;
    }

    // Threshold in the cart's currency, or null when free shipping does not apply / cannot be judged.
    findRule(rules) {
      const countries = rules?.countries;
      if (!countries) return null;
      const rule = countries[this.country] || countries.ROW;
      if (!rule || rule.freeFrom === null || rule.freeFrom === undefined) return null;

      if (rule.currency === this.currency) return { freeFrom: rule.freeFrom, converted: false };
      if (rule.currency === this.dataset.shopCurrency) {
        const rate = parseFloat(window.Shopify?.currency?.rate);
        if (Number.isFinite(rate) && rate > 0) return { freeFrom: rule.freeFrom * rate, converted: true };
      }
      return null;
    }

    ensureThresholdLabel() {
      if (!this.rule.converted || this.querySelector('.pdp-shipping__threshold')) return;
      const template = this.dataset.freeFromTemplate;
      if (!template || !(this.rule.freeFrom > 0)) return;

      let symbol = this.currency;
      try {
        symbol =
          new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol'
          })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value || this.currency;
      } catch (error) {}

      const label = document.createElement('span');
      label.className = 'pdp-shipping__threshold';
      label.textContent = ` – ${template.replace('__AMOUNT__', `${symbol} ${Math.round(this.rule.freeFrom)}`)}`;
      this.paidEl.append(label);
    }

    async getCartTotalCents() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        return (await response.json()).total_price || 0;
      } catch (error) {
        return 0;
      }
    }

    async check() {
      const priceEl = document.querySelector('#rug-configurator-app .price-bar .price, #drawer-configurator-app .price-bar .price, #seatpad-configurator-app .price-bar .price');
      const price = priceEl ? this.parsePrice(priceEl.textContent) : null;

      // Nothing priceable configured yet (no width/length or diameter, or no finish chosen) — hide
      // the note entirely rather than show a shipping note for a €0 order.
      if (price === null) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      if (!this.rule) {
        this.render('paid');
        return;
      }

      const requestId = ++this.requestId;
      const cartCents = await this.getCartTotalCents();
      if (requestId !== this.requestId) return;

      const subtotal = cartCents / 100 + price;
      this.render(subtotal >= this.rule.freeFrom ? 'free' : 'paid');
    }

    // state: 'free' | 'paid'
    render(state) {
      this.freeEl.hidden = state !== 'free';
      this.paidEl.hidden = state === 'free';
    }
  }

  customElements.define('configurator-shipping-note', ConfiguratorShippingNote);
}
